import React, {useEffect} from 'react';
import {
  ActionModal,
  ErrorModal,
  FormInput,
  FileUploadDropzone,
  FilePreviewInformation,
  useErrorHandler,
} from '../../../../shared';
import {useAddPartnerForm} from '../../model';
import {useLogoUpload} from '../../lib';
import {Text} from '@gravity-ui/uikit';
import styles from './AddPartnerModal.module.scss';

interface AddPartnerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export const AddPartnerModal = ({open, setOpen}: AddPartnerModalProps) => {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    isLoading,
    submitError,
    resetForm,
    submitForm,
    setFieldValue,
  } = useAddPartnerForm({setOpen});

  const {uploadId, fileName, fileSize, isUploading, uploadLogo, resetUpload} = useLogoUpload();

  const {isErrorModalOpen, errorInfo, handleError, handleClose} = useErrorHandler({
    resourceName: 'Партнер',
    title: 'Ошибка при добавлении партнера',
  });

  useEffect(() => {
    if (submitError) {
      handleError(submitError);
    }
  }, [submitError, handleError]);

  // Очистка формы при открытии модального окна
  useEffect(() => {
    if (open) {
      resetForm();
      resetUpload();
    }
    // eslint-disable-next-line
  }, [open]);

  // Обновляем uploadId в форме когда логотип загружен
  useEffect(() => {
    if (uploadId) {
      setFieldValue('logoUploadId', uploadId);
    } else {
      setFieldValue('logoUploadId', '');
    }
  }, [uploadId, setFieldValue]);

  const handleFileSelect = (file: File) => {
    uploadLogo(file);
  };

  const handleRemoveFile = () => {
    resetUpload();
    setFieldValue('logoUploadId', '');
  };

  return (
    <>
      <ActionModal
        open={open}
        onClose={() => setOpen(false)}
        setOpen={() => setOpen(false)}
        resetForm={resetForm}
        submitForm={submitForm}
        isLoading={isLoading}
        size={'m'}
        buttonsSize={'l'}
        caption={'Добавить партнера'}
        textButtonApply={'Добавить'}
        textButtonCancel={'Отменить'}>
        <FormInput
          label="Название"
          placeholder="Название партнера"
          name="title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.title && errors.title}
        />
        <FormInput
          label="Описание"
          placeholder="Описание партнера"
          name="description"
          value={values.description}
          onChange={handleChange}
          onBlur={handleBlur}
          isTextarea
          rows={3}
          error={touched.description && errors.description}
        />
        <FormInput
          label="Приоритет"
          placeholder="1"
          name="priority"
          type="number"
          value={values.priority.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          isRequired
          error={touched.priority && errors.priority}
        />
        <FormInput
          label="Сайт"
          placeholder="https://example.com"
          name="website"
          value={values.website}
          onChange={handleChange}
          onBlur={handleBlur}
          error={touched.website && errors.website}
        />

        <div className={styles.fieldContainer}>
          <Text variant="subheader-1" className={styles.label}>
            Логотип <span style={{color: 'red'}}>*</span>
          </Text>

          {!uploadId && !values.logoUploadId ? (
            <FileUploadDropzone
              onFileSelect={handleFileSelect}
              accept=".jpg,.jpeg,.png,.webp"
              maxSize={25 * 1024 * 1024}
              isLoading={isUploading}
            />
          ) : (
            <FilePreviewInformation fileName={fileName} fileSize={fileSize} handleRemoveFile={handleRemoveFile} />
          )}

          {touched.logoUploadId && errors.logoUploadId && (
            <Text variant="caption-2" color="danger" style={{marginTop: '4px'}}>
              {errors.logoUploadId}
            </Text>
          )}
        </div>
      </ActionModal>
      <ErrorModal open={isErrorModalOpen} onClose={handleClose} title={errorInfo.title} message={errorInfo.message} />
    </>
  );
};
